import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { ensureSession } from "@/auth/refresh-loop";

export const Route = createFileRoute("/login")({
  beforeLoad: async () => {
    // On reload `accessToken` is gone (memory-only) but `refreshToken`
    // survives in localStorage. Try to swap it before showing the form.
    if (await ensureSession()) throw redirect({ to: "/app" });
  },
  component: LoginPage,
});

function LoginPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setErrMsg(null);
    try {
      await authApi.magicLink(email.trim().toLowerCase());
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setErrMsg(err instanceof ApiError ? err.message : "Failed to send magic link");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-md">
        <h1 className="text-2xl font-semibold">Sign in</h1>
        <p className="mt-1 text-sm text-ink/65">
          We'll email you a one-time link. No password needed.
        </p>

        {status === "sent" ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-md bg-green-50 p-4 text-sm text-green-900">
              Check <b>{email}</b> for a sign-in link. It expires in 15 minutes.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a
                href="https://mail.google.com/mail/u/0/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <GmailLogo />
                Open Gmail
              </a>
              <a
                href="https://outlook.live.com/mail/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <OutlookLogo />
                Open Outlook
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                className="input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary w-full" disabled={status === "sending"}>
              {status === "sending" ? "Sending…" : "Send magic link"}
            </button>
            {errMsg && (
              <p className="text-sm text-red-600">{errMsg}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

function GmailLogo() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
      <path fill="#4caf50" d="M45 16.2l-5 2.75-5 4.75L35 40h7c1.66 0 3-1.34 3-3V16.2z" />
      <path fill="#1e88e5" d="M3 16.2l3.614 1.71L13 23.7V40H6c-1.66 0-3-1.34-3-3V16.2z" />
      <polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17" />
      <path fill="#c62828" d="M3 12.298V16.2l10 7.5V11.2L9.876 8.859C9.132 8.301 8.228 8 7.298 8 4.924 8 3 9.924 3 12.298z" />
      <path fill="#fbc02d" d="M45 12.298V16.2l-10 7.5V11.2l3.124-2.341C38.868 8.301 39.772 8 40.702 8 43.076 8 45 9.924 45 12.298z" />
    </svg>
  );
}

function OutlookLogo() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
      <path fill="#1066b5" d="M24 24l-2-1.5V11.2c0-.66.54-1.2 1.2-1.2H43c.66 0 1.2.54 1.2 1.2v25.6c0 .66-.54 1.2-1.2 1.2H23.2c-.66 0-1.2-.54-1.2-1.2V24z" />
      <path fill="#fff" d="M28 16h12v3H28zM28 21h12v3H28zM28 26h12v3H28zM28 31h8v3h-8z" />
      <path fill="#28a8ea" d="M22 13L4 9.5v29L22 35z" />
      <path fill="#0078d4" d="M22 13v22l-9-1.8V14.8z" />
      <path fill="#fff" d="M13 18.4c-2.8 0-4.7 2.3-4.7 5.6S10.2 29.6 13 29.6s4.7-2.3 4.7-5.6S15.8 18.4 13 18.4zm0 2.4c1.3 0 2.1 1.3 2.1 3.2S14.3 27.2 13 27.2s-2.1-1.3-2.1-3.2.8-3.2 2.1-3.2z" />
    </svg>
  );
}
